// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { GameLevelResourceService } from '../../../../../../../services/game-level-resource.service';
import { GameLevelItem, GameLevelService } from '../../../../../../../services/game-level.service';
import { SyllabusService } from '../../../../../../../services/syllabus.service';

type SyllabusNode = { id: number; name: string };

@Component({
    selector: 'app-game-level-resources-batch',
    templateUrl: './game-level-resources-batch.component.html',
    styleUrls: ['./game-level-resources-batch.component.scss'],
    standalone: false
})
export class GameLevelResourcesBatchComponent implements OnInit {
  loadingInit = false;
  submitting = false;
  gameLevels: GameLevelItem[] = [];

  pathCascaderOptions: Array<{
    id: number;
    name: string;
    children: Array<SyllabusNode & { isLeaf?: boolean }>;
  }> = [];
  pathCascaderValues: number[] | null = null;

  exercisePackageOptions: SyllabusNode[] = [];
  /** 当前资源路径下题包列表请求中（与「无题库」区分） */
  packagesLoading = false;
  private packagesLoadSeq = 0;

  /** 行唯一 id → 题目选项（与作业模版习题中 level4 列表同源） */
  private nextPackageRowId = 1;
  packageItemOptionsByRowId: Record<number, SyllabusNode[] | null> = {};

  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private gameLevelSvc: GameLevelService,
    private syllabusSvc: SyllabusService,
    private resourceSvc: GameLevelResourceService,
    private router: Router,
    private msg: NzMessageService,
  ) {
    this.form = this.fb.group({
      levelId: [null, Validators.required],
      packages: this.fb.array([]),
    });
  }

  get packages(): FormArray {
    return this.form.get('packages') as FormArray;
  }

  ngOnInit(): void {
    this.loadingInit = true;
    Promise.all([
      this.gameLevelSvc.list().then((list) => {
        this.gameLevels = Array.isArray(list) ? list : [];
      }),
      this.buildPathCascaderOptions(),
    ])
      .then(() => {
        this.loadingInit = false;
      })
      .catch((err: any) => {
        this.loadingInit = false;
        this.msg.error(err?.message || '加载失败');
      });
  }

  private async buildPathCascaderOptions(): Promise<void> {
    this.pathCascaderOptions = await this.syllabusSvc.getExercisePathCascaderOptionsTwoLevels();
  }

  pathLeafId(): number | null {
    const v = this.pathCascaderValues;
    if (!Array.isArray(v) || v.length < 2) {
      return null;
    }
    return v[v.length - 1];
  }

  onPathCascaderChange(_vals: number[] | null): void {
    this.exercisePackageOptions = [];
    this.clearPackages();
    const leaf = this.pathLeafId();
    if (!leaf) {
      this.packagesLoading = false;
      return;
    }
    this.packagesLoading = true;
    const seq = ++this.packagesLoadSeq;
    this.syllabusSvc
      .getExercises(leaf)
      .then((list: SyllabusNode[]) => {
        if (seq !== this.packagesLoadSeq) {
          return;
        }
        this.exercisePackageOptions = Array.isArray(list) ? list : [];
        this.packagesLoading = false;
      })
      .catch((err: any) => {
        if (seq !== this.packagesLoadSeq) {
          return;
        }
        this.packagesLoading = false;
        this.msg.error(err?.message || '加载题包失败');
      });
  }

  canAddPackage(): boolean {
    return !!this.pathLeafId() && this.exercisePackageOptions.length > 0;
  }

  addPackageRow(): void {
    if (!this.canAddPackage()) {
      return;
    }
    const rowId = this.nextPackageRowId++;
    const g = this.fb.group({
      rowId: [rowId],
      exerciseId: [null, Validators.required],
      bankIds: [[]],
    });
    this.packages.push(g);
    this.packageItemOptionsByRowId[rowId] = null;

    g.get('exerciseId')!.valueChanges.subscribe((exerciseId: number | null) => {
      this.onPackageExerciseChange(rowId, exerciseId);
    });
  }

  removePackageRow(index: number): void {
    const g = this.packages.at(index) as FormGroup;
    const rowId = g.get('rowId')?.value;
    if (rowId != null) {
      delete this.packageItemOptionsByRowId[rowId];
    }
    this.packages.removeAt(index);
  }

  clearPackages(): void {
    while (this.packages.length) {
      this.packages.removeAt(0);
    }
    this.packageItemOptionsByRowId = {};
    this.nextPackageRowId = 1;
  }

  private onPackageExerciseChange(rowId: number, exerciseId: number | null): void {
    const g = this.findPackageGroupByRowId(rowId);
    if (g) {
      g.patchValue({ bankIds: [] });
    }
    this.packageItemOptionsByRowId[rowId] = null;
    if (!exerciseId) {
      return;
    }
    this.syllabusSvc.getExerciseItems(exerciseId).then((list: SyllabusNode[]) => {
      this.packageItemOptionsByRowId[rowId] = Array.isArray(list) ? list : [];
    });
  }

  private findPackageGroupByRowId(rowId: number): FormGroup | null {
    for (let i = 0; i < this.packages.length; i++) {
      const fg = this.packages.at(i) as FormGroup;
      if (fg.get('rowId')?.value === rowId) {
        return fg;
      }
    }
    return null;
  }

  getPkgControl(i: number, name: string): FormControl {
    return (this.packages.at(i) as FormGroup).get(name) as FormControl;
  }

  itemOptionsForRow(rowId: number): SyllabusNode[] {
    return this.packageItemOptionsByRowId[rowId] || [];
  }

  cancel(): void {
    this.router.navigate(['/admin/v3/game/exercise-bank']);
  }

  submit(): void {
    if (this.form.get('levelId')?.invalid) {
      this.msg.warning('请选择级别');
      return;
    }

    const levelId = this.form.get('levelId')?.value;
    const leaf = this.pathLeafId();
    if (!leaf) {
      this.msg.warning('请选择资源路径（需选到二级叶子）');
      return;
    }

    if (this.packages.length === 0) {
      this.msg.warning('请至少添加一个题包并勾选题目');
      return;
    }

    const allBankIds: number[] = [];
    for (let i = 0; i < this.packages.length; i++) {
      const g = this.packages.at(i) as FormGroup;
      const exId = g.get('exerciseId')?.value;
      const ids: number[] = g.get('bankIds')?.value || [];
      if (!exId) {
        this.msg.warning(`第 ${i + 1} 个题包未选择题包`);
        return;
      }
      if (!ids || ids.length === 0) {
        this.msg.warning(`第 ${i + 1} 个题包请至少勾选一道题目`);
        return;
      }
      allBankIds.push(...ids);
    }

    const rltIds = [...new Set(allBankIds)];
    if (rltIds.length === 0) {
      this.msg.warning('请至少勾选一道题目');
      return;
    }

    this.submitting = true;
    this.resourceSvc
      .batchLevelResources({
        levelId,
        rltType: 'exercise',
        resources: [{ rltSid: leaf, rltIds }],
      })
      .then((res) => {
        this.msg.success(`提交成功，本次新增 ${res?.insertedCount ?? 0} 条`);
        this.submitting = false;
        this.router.navigate(['/admin/v3/game/exercise-bank']);
      })
      .catch((err: any) => {
        this.submitting = false;
        this.msg.error(err?.message || '提交失败');
      });
  }
}
